import React, {Component} from 'react';
import PropTypes from 'prop-types';
import Checkbox from '@spothero/ui/checkbox';
import Form from '@spothero/ui/form';
import FormGroup from '@spothero/ui/form-group';
import FormGroupError from '@spothero/ui/form-group-error';
import Label from '@spothero/ui/label';
import APIUtils from '@spothero/utils/api';
import VehicleAPI from 'api/vehicle';
import UserUtils from 'user/user-utils';
import SegmentUtils from 'utils/segment';
import VehicleSelect from './VehicleSelect';
import SentryUtils from '@spothero/utils/sentry';
import get from 'lodash/get';

class AddNewVehicleForm extends Component {
    static propTypes = {
        user: PropTypes.object,
        showTitle: PropTypes.bool,
        formErrors: PropTypes.array,
        setOversizeFeeError: PropTypes.func,
        vehicleSelectErrorsInline: PropTypes.bool,
        useVehicleSelectLabels: PropTypes.bool,
        showSaveAsDefault: PropTypes.bool,
        fromAccount: PropTypes.bool,
    };
    static defaultProps = {
        showSaveAsDefault: true,
    };
    state = {
        formError: null,
    };

    componentWillUnmount() {
        if (this._addVehicleLoader) {
            this._addVehicleLoader.cancel();
            this._addVehicleLoader = null;
        }

        if (this._vehicleInfosLoader) {
            this._vehicleInfosLoader.cancel();
            this._vehicleInfosLoader = null;
        }
    }

    async _addVehicle(formData) {
        const {
            vehicleInfoId,
            licensePlate,
            licenseState,
            saveVehicleAsDefault,
            unlistedModel,
            search,
        } = formData;
        const {fromAccount, user} = this.props;

        const vehicleInfoToSubmit = {
            license_plate: licensePlate, // eslint-disable-line camelcase
            license_plate_state: licenseState, // eslint-disable-line camelcase
            is_default: Boolean(saveVehicleAsDefault), // eslint-disable-line camelcase
        };

        if (vehicleInfoId) {
            vehicleInfoToSubmit.vehicle_info_id = vehicleInfoId; // eslint-disable-line camelcase
        }
        if (unlistedModel) {
            vehicleInfoToSubmit.unlisted_model = unlistedModel; // eslint-disable-line camelcase
        }

        if (!user.id) {
            const vehicleInfoToSave = {
                licensePlateState: licenseState,
                licensePlateValue: licensePlate,
                unlistedModel,
            };

            if (vehicleInfoId) {
                vehicleInfoToSave.vehicleInfoId = vehicleInfoId;
            }

            if (unlistedModel) {
                vehicleInfoToSave.unlistedModel = unlistedModel;
            }

            vehicleInfoToSave.vehicleMakeModelPlate =
                search !== 'Vehicle not listed'
                    ? `${search} | ${licensePlate}`
                    : `No Vehicle | ${licensePlate}`;

            return vehicleInfoToSave;
        } else {
            try {
                this._addVehicleLoader = APIUtils.makeCancelable(
                    VehicleAPI.add(vehicleInfoToSubmit, user.id)
                );

                const {
                    data: {data},
                } = await this._addVehicleLoader.promise;

                const vehicleInfoToSave = {
                    vehicleProfileId: data.id,
                    isDefault: data.is_default,
                    licensePlateState: data.license_plate.state,
                    licensePlateValue: data.license_plate.value,
                    unlistedModel: data.unlisted_model,
                };
                const vehicleInfo = data.info;

                if (vehicleInfo) {
                    vehicleInfoToSave.vehicleMakeModelPlate = `${vehicleInfo.display_make} ${vehicleInfo.display_model} | ${data.license_plate.value}`;
                } else {
                    vehicleInfoToSave.vehicleMakeModelPlate = `No Vehicle | ${data.license_plate.value}`;
                }

                SegmentUtils.track({
                    event: 'Account Step Completed',
                    properties: {
                        'vehicle updated': true,
                        'from screen': fromAccount ? 'account' : 'checkout',
                    },
                });

                return vehicleInfoToSave;
            } catch (error) {
                this.setState({
                    formError: `Oops! We couldn't save that vehicle.`,
                });

                SegmentUtils.track({
                    event: 'Error Message Displayed',
                    properties: {
                        'error message': `Oops! We couldn't save that vehicle.`,
                        'error screen': fromAccount ? 'account' : 'checkout',
                        'error action': 'updating vehicle',
                    },
                });
            }

            this._addVehicleLoader = null;
        }
    }

    async submit() {
        const vehicleSelectData = this._vehicleSelectForm
            ? this._vehicleSelectForm.submit()
            : false;

        if (!vehicleSelectData) {
            return false;
        }

        if (
            !vehicleSelectData.vehicleInfoId &&
            !vehicleSelectData.unlistedModel
        ) {
            this.setState({
                formError:
                    'Please select a vehicle. If your vehicle is not listed, select the "Vehicle not listed" option.',
            });

            return false;
        }

        try {
            const newVehicleData = await this._addVehicle({
                ...vehicleSelectData,
                ...this._defaultVehicleForm.submit(),
            });

            if (newVehicleData && newVehicleData.licensePlateValue) {
                return newVehicleData;
            } else {
                return false;
            }
        } catch (error) {
            const errorMessage =
                error &&
                (error.message ||
                    error.statusText ||
                    get(error, 'response.data', ''));

            SentryUtils.addData({
                caughtError: error,
            });
            SentryUtils.sendMessage({
                message: errorMessage,
                level: 'error',
            });

            return false;
        }
    }

    render() {
        const {
            user,
            showTitle,
            formErrors,
            setOversizeFeeError,
            vehicleSelectErrorsInline,
            useVehicleSelectLabels,
            showSaveAsDefault,
        } = this.props;
        const {formError} = this.state;
        const errors = formErrors ? formErrors : formError;

        return (
            <div className="AddNewVehicleForm">
                {showTitle && <h2 className="subtitle">Add a Vehicle</h2>}
                <VehicleSelect
                    ref={node => {
                        this._vehicleSelectForm = node;
                    }}
                    setOversizeFeeError={setOversizeFeeError}
                    errorsInline={vehicleSelectErrorsInline}
                    useLabels={useVehicleSelectLabels}
                    user={user}
                />
                <Form
                    ref={node => {
                        this._defaultVehicleForm = node;
                    }}
                    id="AddNewVehicleForm-form"
                >
                    {user.status === UserUtils.AUTH_STATE.USER &&
                        showSaveAsDefault && (
                            <FormGroup>
                                <Checkbox
                                    id="AddNewVehicleForm-save-as-default"
                                    name="saveVehicleAsDefault"
                                    label={
                                        <Label>
                                            Make this my default vehicle
                                        </Label>
                                    }
                                    defaultChecked
                                />
                            </FormGroup>
                        )}
                    {errors && <FormGroupError>{errors}</FormGroupError>}
                </Form>
            </div>
        );
    }
}

export default AddNewVehicleForm;
